import { useState, useEffect, useRef } from "react";
import { verificarDni } from "../services/api";
import { cleanDni, formatDni } from "../utils/formatDni";

const RESET_SECONDS = 12;

function StatusCard({ result, onReset }) {
  const [seconds, setSeconds] = useState(RESET_SECONDS);

  useEffect(() => {
    const t = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(t); onReset(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [onReset]);

  if (!result.encontrado) {
    return (
      <div className="status-card not-found">
        <div className="status-icon">✗</div>
        <div className="status-title">DNI no encontrado</div>
        <div className="status-sub">No hay ningún socio registrado con ese DNI.</div>
        <div className="reset-bar">
          <div className="reset-fill" style={{ animationDuration: `${RESET_SECONDS}s` }} />
        </div>
        <div className="reset-label">Vuelve en {seconds}s</div>
      </div>
    );
  }

  const activo = result.activo;

  if (!activo) {
    return (
      <div className="status-card inactive">
        <div className="status-icon">✗</div>
        <div className="status-name">{result.nombre}</div>
        <div className="status-title">✗ Membresía inactiva</div>
        <div className="status-sub">{result.mensaje_inactivo}</div>
        <div className="reset-bar">
          <div className="reset-fill" style={{ animationDuration: `${RESET_SECONDS}s` }} />
        </div>
        <div className="reset-label">Cerrando en {seconds}s</div>
      </div>
    );
  }

  const fechaFinStr = result.fecha_fin
    ? new Date(result.fecha_fin + "T00:00:00").toLocaleDateString("es-AR")
    : null;

  return (
    <div className="status-card active">
      <div className="status-icon">✓</div>
      <div className="status-name">{result.nombre}</div>
      <div className="status-title">Socio Activo</div>
      {result.plan_nombre && <div className="status-sub">{result.plan_nombre}</div>}
      <div className="status-details">
        {result.dias_restantes !== null && (
          <span className="detail-chip">
            {result.dias_restantes === 0 ? "Vence hoy" : `Vence en ${result.dias_restantes}d`}
          </span>
        )}
        {fechaFinStr && <span className="detail-chip">hasta {fechaFinStr}</span>}
        <span className="detail-chip">
          {result.creditos_restantes}
          {result.plan_creditos_totales != null ? `/${result.plan_creditos_totales}` : ""} créditos
        </span>
        {result.credito_descontado && (
          <span className="detail-chip chip-ok">✓ 1 crédito descontado</span>
        )}
        {result.ya_registrado_hoy && (
          <span className="detail-chip">Ya registrado hoy</span>
        )}
      </div>
      {result.sin_creditos && (
        <div className="status-banner">Sin créditos disponibles. Consultá con el dueño.</div>
      )}
      {result.pago_pendiente && (
        <div className="status-banner">{result.mensaje_pago_pendiente}</div>
      )}
      <div className="reset-bar">
        <div className="reset-fill" style={{ animationDuration: `${RESET_SECONDS}s` }} />
      </div>
      <div className="reset-label">Cerrando en {seconds}s</div>
    </div>
  );
}

export default function Verificar() {
  const [dni, setDni] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleReset = () => {
    setResult(null);
    setDni("");
    setError("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = dni.trim();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    try {
      const { ok, data } = await verificarDni(trimmed);
      if (ok || data.encontrado === false) {
        setResult(data);
      } else {
        setError(data.error || "Error al verificar");
      }
    } catch {
      setError("No se pudo conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0f172a; }
        html, body, #root { height: 100%; }
        .verificar-page {
          min-height: 100vh;
          width: 100%;
          background: #0f172a;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3vh 3vw;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .verificar-logo {
          color: #94a3b8;
          font-size: clamp(1.1rem, 2vw, 1.7rem);
          font-weight: 600;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          margin-bottom: clamp(2rem, 4vh, 3.5rem);
        }
        .verificar-card {
          background: #1e293b;
          border-radius: 2rem;
          padding: clamp(2.5rem, 5vw, 4.5rem) clamp(2rem, 5vw, 4rem);
          width: 100%;
          max-width: min(94vw, 960px);
          box-shadow: 0 25px 50px rgba(0,0,0,0.5);
        }
        .verificar-title {
          color: #f1f5f9;
          font-size: clamp(2rem, 4.5vw, 3.2rem);
          font-weight: 700;
          text-align: center;
          margin-bottom: 0.75rem;
        }
        .verificar-sub {
          color: #64748b;
          font-size: clamp(1.1rem, 2vw, 1.5rem);
          text-align: center;
          margin-bottom: clamp(2rem, 4vh, 3rem);
        }
        .dni-input {
          width: 100%;
          background: #0f172a;
          border: 3px solid #334155;
          border-radius: 1rem;
          color: #f1f5f9;
          font-size: clamp(2.5rem, 7vw, 4.5rem);
          font-weight: 700;
          letter-spacing: 0.2em;
          text-align: center;
          padding: clamp(1.2rem, 3vh, 2rem);
          outline: none;
          transition: border-color 0.2s;
        }
        .dni-input:focus { border-color: #6366f1; }
        .dni-input::placeholder { color: #475569; font-size: clamp(1.2rem, 2.5vw, 1.8rem); letter-spacing: 0.05em; font-weight: 400; }
        .submit-btn {
          width: 100%;
          margin-top: clamp(1.25rem, 2.5vh, 2rem);
          padding: clamp(1.2rem, 2.5vh, 1.8rem);
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 1rem;
          font-size: clamp(1.3rem, 2.5vw, 1.8rem);
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, opacity 0.2s;
        }
        .submit-btn:hover:not(:disabled) { background: #4f46e5; }
        .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .error-msg {
          margin-top: 1.25rem;
          color: #f87171;
          text-align: center;
          font-size: clamp(1rem, 1.8vw, 1.4rem);
        }
        .status-card {
          text-align: center;
          padding: 1rem 0 0.5rem;
        }
        .status-card.active .status-icon { color: #4ade80; }
        .status-card.inactive .status-icon,
        .status-card.not-found .status-icon { color: #f87171; }
        .status-icon { font-size: clamp(5rem, 11vw, 8.5rem); margin-bottom: 1rem; }
        .status-name { color: #f1f5f9; font-size: clamp(2.2rem, 5vw, 3.6rem); font-weight: 700; margin-bottom: 0.4rem; }
        .status-title { font-size: clamp(1.5rem, 3vw, 2.2rem); font-weight: 600; margin-bottom: 0.75rem; }
        .status-card.active .status-title { color: #4ade80; }
        .status-card.inactive .status-title,
        .status-card.not-found .status-title { color: #f87171; }
        .status-sub { color: #64748b; font-size: clamp(1.1rem, 2.2vw, 1.6rem); margin-bottom: 1.5rem; }
        .status-details { display: flex; gap: 0.75rem; justify-content: center; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .detail-chip { background: #334155; color: #94a3b8; padding: 0.5rem 1.2rem; border-radius: 9999px; font-size: clamp(1rem, 1.8vw, 1.35rem); }
        .detail-chip.chip-ok { background: rgba(74,222,128,0.15); color: #4ade80; }
        .status-banner { background: rgba(251,191,36,0.12); border: 1px solid rgba(251,191,36,0.35); color: #fbbf24; border-radius: 1rem; padding: 1rem 1.4rem; font-size: clamp(1rem, 1.8vw, 1.35rem); margin-bottom: 1rem; }
        .reset-bar { background: #0f172a; border-radius: 9999px; height: 6px; overflow: hidden; margin-top: 1.75rem; }
        .reset-fill { height: 100%; background: #6366f1; border-radius: 9999px; width: 100%; animation: drain linear forwards; }
        @keyframes drain { from { width: 100%; } to { width: 0%; } }
        .reset-label { color: #475569; font-size: clamp(0.9rem, 1.4vw, 1.15rem); text-align: center; margin-top: 0.6rem; }
      `}</style>
      <div className="verificar-page">
        <div className="verificar-logo">FitAdmin · Verificación</div>
        <div className="verificar-card">
          {result ? (
            <StatusCard result={result} onReset={handleReset} />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="verificar-title">Verificar Socio</div>
              <div className="verificar-sub">Ingresá tu DNI para consultar tu estado</div>
              <input
                ref={inputRef}
                className="dni-input"
                type="text"
                inputMode="numeric"
                placeholder="Ej: 12.345.678"
                value={formatDni(dni)}
                onChange={(e) => setDni(cleanDni(e.target.value))}
                maxLength={13}
                autoComplete="off"
              />
              <button className="submit-btn" type="submit" disabled={loading || !dni.trim()}>
                {loading ? "Verificando…" : "Consultar"}
              </button>
              {error && <div className="error-msg">{error}</div>}
            </form>
          )}
        </div>
      </div>
    </>
  );
}
